import React, { useEffect, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { groundHeight } from './Landscape';
import { HALL_WINDOWS } from './HallWindows';

// Puddles on the lawn outside the windows, and raindrops splashing on the ground.
//   • Heavy rain fills the puddles (about 40 s to full); afterwards they dry over a few minutes —
//     faster in sunshine and wind, slower under clouds. Snow covers them.
//   • While it rains, rings ripple across the water; the rest of the time it mirrors the sky.
//   • Deer and foxes come to drink while there is water (see Wildlife.jsx, which reads PUDDLES and
//     weatherRef.current.puddles).
// One instanced mesh for all puddles and one point cloud for the splashes; both always exist (the
// puddles are scaled to zero, the splashes use drawRange), so nothing compiles mid-visit.

const IS_MOBILE = typeof window !== 'undefined' && (window.innerWidth < 768 || /iPhone|iPad|Android/i.test(navigator.userAgent));

const SPOTS = [
  ...HALL_WINDOWS.north.map((x) => ({ x, z: -12, nx: 0, nz: -1 })),
  ...HALL_WINDOWS.east.map((x) => ({ x: 12, z: x, nx: 1, nz: 0 })),
  ...HALL_WINDOWS.west.map((x) => ({ x: -12, z: -x, nx: -1, nz: 0 })),
].map((s, i) => ({ ...s, tx: -s.nz, tz: s.nx, index: i }));

let seed = 5;
const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);

// Puddle spots: one or two in front of each window, on the flat lawn (3–10 m out from the wall).
export const PUDDLES = SPOTS.flatMap((s) => (IS_MOBILE ? [0] : [0, 1]).map((k) => {
  const out = k === 0 ? 3.5 + rnd() * 2.5 : 7 + rnd() * 3;
  const lat = (rnd() - 0.5) * (k === 0 ? 3 : 6);
  const x = s.x + s.nx * out + s.tx * lat, z = s.z + s.nz * out + s.tz * lat;
  return { x, z, y: groundHeight(x, z) + 0.03, r: 0.9 + rnd() * 0.9, sx: 0.8 + rnd() * 0.5, rot: rnd() * Math.PI, window: s.index };
}));

// An irregular, flat blob (radius about 1) with a radial coordinate for the shader (0 centre … 1 rim).
const makeBlob = () => {
  const SEG = 48;
  const pos = [0, 0, 0], rad = [0], idx = [];
  const ph = [rnd() * 6, rnd() * 6, rnd() * 6];
  for (let i = 0; i <= SEG; i++) {
    const a = (i / SEG) * Math.PI * 2;
    const r = 1 + 0.16 * Math.sin(a * 2 + ph[0]) + 0.09 * Math.sin(a * 3 + ph[1]) + 0.05 * Math.sin(a * 7 + ph[2]);
    pos.push(Math.cos(a) * r, 0, Math.sin(a) * r);
    rad.push(1);
    if (i > 0) idx.push(0, i + 1, i);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(new Array(pos.length).fill(0).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
  g.setAttribute('aRim', new THREE.Float32BufferAttribute(rad, 1));
  g.setIndex(idx);
  return g;
};

const makeWaterMaterial = (uniforms) => {
  const m = new THREE.MeshStandardMaterial({ color: '#2f3d44', metalness: 0.85, roughness: 0.06, envMapIntensity: 1.3 });
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aRim;\nvarying float vRim;\nvarying vec3 vWorld;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvRim = aRim;')
      .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvWorld = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform float uRain;
        uniform float uTime;
        varying float vRim;
        varying vec3 vWorld;
        float pHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }`)
      // muddy, rougher rim
      .replace('#include <color_fragment>', `#include <color_fragment>
        float rim = smoothstep(0.7, 1.0, vRim);
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.16, 0.13, 0.1), rim);`)
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nroughnessFactor = mix(roughnessFactor, 0.7, smoothstep(0.75, 1.0, vRim));')
      // raindrop rings: every 0.4 m cell has a ripple growing and fading at its own pace
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
        if (uRain > 0.01) {
          vec2 ripple = vec2(0.0);
          for (int k = 0; k < 2; k++) {
            vec2 q = vWorld.xz / 0.4 + float(k) * 0.5;
            vec2 cell = floor(q);
            vec2 centre = cell + 0.25 + 0.5 * vec2(pHash(cell), pHash(cell + 7.0));
            float life = fract(uTime * (0.9 + pHash(cell + 3.0)) + pHash(cell + 11.0));
            vec2 d = q - centre;
            float dist = length(d);
            float ring = sin((dist - life * 0.55) * 40.0) * smoothstep(0.08, 0.0, abs(dist - life * 0.55)) * (1.0 - life);
            ripple += normalize(d + 1e-4) * ring;
          }
          normal = normalize(normal + (viewMatrix * vec4(ripple.x, 0.0, ripple.y, 0.0)).xyz * 0.6 * uRain); // world → view space
        }`);
  };
  m.customProgramCacheKey = () => 'puddles';
  return m;
};

const HIDDEN = new THREE.Matrix4().makeScale(0, 0, 0);

export const Puddles = ({ weatherRef }) => {
  const count = PUDDLES.length;
  const uniforms = useMemo(() => ({ uRain: { value: 0 }, uTime: { value: 0 } }), []);
  const { mesh, splash } = useMemo(() => {
    const geometry = makeBlob();
    const material = makeWaterMaterial(uniforms);
    const m = new THREE.InstancedMesh(geometry, material, count);
    m.frustumCulled = false;
    m.renderOrder = 0;
    for (let i = 0; i < count; i++) m.setMatrixAt(i, HIDDEN);

    // Splashes: short-lived bright specks on the ground in front of the windows (and on the puddles).
    const n = IS_MOBILE ? 220 : 450;
    const pos = new Float32Array(n * 3), col = new Float32Array(n * 3), life = new Float32Array(n);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.setDrawRange(0, 0);
    const c = document.createElement('canvas'); c.width = c.height = 32;
    const ctx = c.getContext('2d');
    const grad = ctx.createRadialGradient(16, 16, 2, 16, 16, 15);
    grad.addColorStop(0, 'rgba(255,255,255,0.0)'); grad.addColorStop(0.55, 'rgba(255,255,255,0.9)'); grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad; ctx.fillRect(0, 0, 32, 32); // a little ring, like a drop hitting water
    const tex = new THREE.CanvasTexture(c);
    const pm = new THREE.PointsMaterial({ size: 0.1, map: tex, vertexColors: true, transparent: false, depthWrite: false, blending: THREE.AdditiveBlending });
    const points = new THREE.Points(g, pm);
    points.frustumCulled = false;
    for (let i = 0; i < n; i++) life[i] = Math.random();
    return { mesh: m, splash: { points, g, pm, tex, life, n } };
  }, [count, uniforms]);

  useEffect(() => () => {
    mesh.geometry.dispose(); mesh.material.dispose(); mesh.dispose();
    splash.g.dispose(); splash.pm.dispose(); splash.tex.dispose();
  }, [mesh, splash]);

  const tmp = useMemo(() => ({ m: new THREE.Matrix4(), q: new THREE.Quaternion(), p: new THREE.Vector3(), s: new THREE.Vector3(), up: new THREE.Vector3(0, 1, 0), lastLevel: -1 }), []);

  useFrame((state, delta) => {
    const w = weatherRef.current;
    const dt = Math.min(delta, 0.1);
    const rain = Math.max(w.rain || 0, w.storm || 0);
    // fill in heavy rain, otherwise dry: sun and wind dry them faster, clouds slower, snow covers them
    if (w.puddles === undefined) w.puddles = rain > 0.6 ? 0.7 : 0;
    if (rain > 0.6) w.puddles = Math.min(1, w.puddles + (dt / 40) * rain);
    else {
      const dry = (1 / 170) * (1 + 1.5 * (w.wind || 0)) * (1.3 - 0.7 * (w.cloud || 0)) * (1 + 3 * (w.snow || 0));
      w.puddles = Math.max(0, w.puddles - dt * dry);
    }
    uniforms.uRain.value = rain;
    uniforms.uTime.value = state.clock.elapsedTime;

    // puddles grow from their centre as they fill
    const level = Math.round(w.puddles * 200) / 200;
    if (level !== tmp.lastLevel) {
      tmp.lastLevel = level;
      const k = Math.sqrt(level); // area grows with the water
      PUDDLES.forEach((pd, i) => {
        if (k < 0.02) { mesh.setMatrixAt(i, HIDDEN); return; }
        tmp.p.set(pd.x, pd.y, pd.z);
        tmp.q.setFromAxisAngle(tmp.up, pd.rot);
        tmp.s.set(pd.r * k * pd.sx, 1, pd.r * k);
        tmp.m.compose(tmp.p, tmp.q, tmp.s);
        mesh.setMatrixAt(i, tmp.m);
      });
      mesh.instanceMatrix.needsUpdate = true;
    }

    // splashes
    const { g, life, n } = splash;
    const active = rain > 0.05 ? Math.round(n * Math.min(1, rain)) : 0;
    g.setDrawRange(0, active);
    if (active === 0) return;
    const pos = g.attributes.position.array, col = g.attributes.color.array;
    for (let i = 0; i < active; i++) {
      life[i] += dt * 4.5;
      if (life[i] >= 1) {
        life[i] = 0;
        let x, z, y;
        if (i % 4 === 0 && w.puddles > 0.1) { // on a puddle
          const pd = PUDDLES[(Math.random() * PUDDLES.length) | 0];
          const a = Math.random() * Math.PI * 2, r = Math.sqrt(Math.random()) * pd.r * Math.sqrt(w.puddles) * 0.8;
          x = pd.x + Math.cos(a) * r; z = pd.z + Math.sin(a) * r; y = pd.y + 0.02;
        } else { // on the lawn in front of a window
          const s = SPOTS[(Math.random() * SPOTS.length) | 0];
          const out = 1.5 + Math.random() * 14, lat = (Math.random() - 0.5) * (4 + out * 0.6);
          x = s.x + s.nx * out + s.tx * lat; z = s.z + s.nz * out + s.tz * lat; y = groundHeight(x, z) + 0.05;
        }
        pos[i * 3] = x; pos[i * 3 + 1] = y; pos[i * 3 + 2] = z;
      }
      const b = Math.sin(life[i] * Math.PI) * 0.4 * rain;
      col[i * 3] = b; col[i * 3 + 1] = b; col[i * 3 + 2] = b * 1.05;
    }
    g.attributes.position.needsUpdate = true;
    g.attributes.color.needsUpdate = true;
  });

  return (
    <group>
      <primitive object={mesh} />
      <primitive object={splash.points} />
    </group>
  );
};

export default Puddles;
